/**
 * Student Support / Help Center Page
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  HelpCircle, Send, ArrowLeft, Plus, MessageSquare, Clock,
  CheckCircle, AlertCircle, ChevronRight, RefreshCw,
  AlertTriangle, ArrowUp, Paperclip, X, Image as ImageIcon
} from 'lucide-react';
import { AttachmentViewer } from '../components/AttachmentViewer';

const API_BASE = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const priorityConfig = {
  low: { color: 'bg-slate-500/20 text-slate-300', label: 'Low' },
  medium: { color: 'bg-blue-500/20 text-blue-300', label: 'Medium' },
  high: { color: 'bg-orange-500/20 text-orange-300', label: 'High', icon: ArrowUp },
  urgent: { color: 'bg-red-500/20 text-red-300', label: 'Urgent', icon: AlertTriangle }
};

const statusConfig = {
  open: { color: 'bg-amber-500/20 text-amber-300', icon: AlertCircle, label: 'Open' },
  in_progress: { color: 'bg-blue-500/20 text-blue-300', icon: Clock, label: 'In Progress' },
  closed: { color: 'bg-emerald-500/20 text-emerald-300', icon: CheckCircle, label: 'Closed' }
};

const SupportPage = () => {
  const { token } = useAuth();
  const [view, setView] = useState('list');
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ticketDetail, setTicketDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState('general');
  const [priority, setPriority] = useState('medium');
  const [creating, setCreating] = useState(false);
  const [createFiles, setCreateFiles] = useState([]);

  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [attachFiles, setAttachFiles] = useState([]);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  const authHeader = { 'Authorization': `Bearer ${token}` };

  const fetchTickets = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/tickets/my`, { headers: authHeader });
      if (res.ok) setTickets((await res.json()).tickets);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchTickets(); }, [fetchTickets]);

  const handleCreate = async () => {
    if (!subject.trim() || !message.trim()) { toast.error('Subject aur message zaroori hain'); return; }
    setCreating(true);
    try {
      const res = await fetch(`${API_BASE}/api/tickets/create`, {
        method: 'POST', headers,
        body: JSON.stringify({ subject, message, category, priority })
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Failed');

      // If files selected, upload them as a reply-with-attachment
      if (createFiles.length > 0) {
        const formData = new FormData();
        formData.append('message', '');
        createFiles.forEach(f => formData.append('files', f));
        await fetch(`${API_BASE}/api/tickets/${d.ticket_id}/reply-with-attachment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
      }

      toast.success(`Ticket #${d.ticket_no} created!`);
      setSubject(''); setMessage(''); setCategory('general'); setPriority('medium'); setCreateFiles([]);
      setView('list'); fetchTickets();
    } catch (e) { toast.error(e.message); }
    finally { setCreating(false); }
  };

  const openTicket = async (ticket) => {
    setView('detail'); setDetailLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/tickets/${ticket.id}`, { headers: authHeader });
      if (res.ok) setTicketDetail(await res.json());
    } catch (e) { toast.error('Failed to load ticket'); }
    finally { setDetailLoading(false); }
  };

  const handleReply = async () => {
    if (!replyText.trim() && attachFiles.length === 0) return;
    setReplying(true);
    try {
      if (attachFiles.length > 0) {
        // Send with attachments using multipart form
        const formData = new FormData();
        formData.append('message', replyText.trim());
        attachFiles.forEach(f => formData.append('files', f));
        const res = await fetch(`${API_BASE}/api/tickets/${ticketDetail.id}/reply-with-attachment`, {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData
        });
        if (res.ok) { toast.success('Reply sent!'); setReplyText(''); setAttachFiles([]); openTicket({ id: ticketDetail.id }); fetchTickets(); }
        else {
          const err = await res.json();
          toast.error(err.detail || 'Upload failed');
        }
      } else {
        const res = await fetch(`${API_BASE}/api/tickets/${ticketDetail.id}/reply`, {
          method: 'POST', headers,
          body: JSON.stringify({ message: replyText })
        });
        if (res.ok) { toast.success('Reply sent!'); setReplyText(''); openTicket({ id: ticketDetail.id }); fetchTickets(); }
      }
    } catch (e) { toast.error('Failed'); }
    finally { setReplying(false); }
  };

  const handleFileSelect = (e, target = 'reply') => {
    const selected = Array.from(e.target.files || []);
    const current = target === 'create' ? createFiles : attachFiles;
    const setter = target === 'create' ? setCreateFiles : setAttachFiles;
    if (selected.length + current.length > 2) {
      toast.error('Max 2 images allowed');
      return;
    }
    const valid = selected.filter(f => {
      if (f.size > 2 * 1024 * 1024) { toast.error(`${f.name}: File too large (max 2MB)`); return false; }
      if (!['image/png', 'image/jpeg'].includes(f.type)) { toast.error(`${f.name}: Only PNG/JPG allowed`); return false; }
      return true;
    });
    setter(prev => [...prev, ...valid].slice(0, 2));
    e.target.value = '';
  };

  const removeFile = (idx, target = 'reply') => {
    const setter = target === 'create' ? setCreateFiles : setAttachFiles;
    setter(prev => prev.filter((_, i) => i !== idx));
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-4 sm:p-6" data-testid="support-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {view !== 'list' && (
              <Button variant="ghost" size="icon" onClick={() => { setView('list'); setTicketDetail(null); }} className="text-gray-600">
                <ArrowLeft className="w-5 h-5" />
              </Button>
            )}
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <HelpCircle className="w-6 h-6 text-indigo-600" /> Help Center
              </h1>
              <p className="text-sm text-gray-500">Need help? Raise a ticket and we'll get back to you</p>
            </div>
          </div>
          {view === 'list' && (
            <Button onClick={() => setView('create')} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="create-ticket-btn">
              <Plus className="w-4 h-4 mr-2" />New Ticket
            </Button>
          )}
        </div>

        {/* Create Ticket */}
        {view === 'create' && (
          <Card className="border-indigo-200 shadow-lg" data-testid="create-ticket-form">
            <CardHeader><CardTitle className="text-gray-900">New Support Ticket</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Category</label>
                  <select value={category} onChange={e => setCategory(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white" data-testid="ticket-category">
                    <option value="general">General</option>
                    <option value="technical">Technical Issue</option>
                    <option value="billing">Billing / Payment</option>
                    <option value="lab">Lab / VM Issue</option>
                    <option value="course">Course Content</option>
                  </select>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-700 mb-1 block">Priority</label>
                  <select value={priority} onChange={e => setPriority(e.target.value)} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 bg-white" data-testid="ticket-priority">
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Subject</label>
                <Input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Brief description of your issue" className="border-gray-300" data-testid="ticket-subject" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Message</label>
                <textarea value={message} onChange={e => setMessage(e.target.value)} placeholder="Describe your issue in detail..." rows={5} className="w-full border border-gray-300 rounded-lg px-3 py-2 text-gray-900 resize-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-testid="ticket-message" />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700 mb-1 block">Attach Screenshot (optional)</label>
                <div className="flex items-center gap-2 flex-wrap">
                  <label className="cursor-pointer flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors" data-testid="create-attach-btn">
                    <Paperclip className="w-4 h-4" /> Choose Image
                    <input type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={e => handleFileSelect(e, 'create')} />
                  </label>
                  <span className="text-xs text-gray-400">PNG/JPG only, max 2MB, max 2 files</span>
                </div>
                {createFiles.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {createFiles.map((f, i) => (
                      <div key={i} className="flex items-center gap-1 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 text-xs text-indigo-700">
                        <ImageIcon className="w-3 h-3" />
                        <span className="max-w-[150px] truncate">{f.name}</span>
                        <button onClick={() => removeFile(i, 'create')} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button onClick={handleCreate} disabled={creating || !subject.trim() || !message.trim()} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="submit-ticket-btn">
                  <Send className="w-4 h-4 mr-2" />{creating ? 'Sending...' : 'Submit Ticket'}
                </Button>
                <Button variant="outline" onClick={() => { setView('list'); setCreateFiles([]); }}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ticket List */}
        {view === 'list' && (
          <div className="space-y-3" data-testid="ticket-list">
            {loading ? (
              <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" /></div>
            ) : tickets.length === 0 ? (
              <Card className="border-gray-200">
                <CardContent className="py-16 text-center">
                  <MessageSquare className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-gray-700 mb-2">No tickets yet</h3>
                  <p className="text-gray-500 mb-6">Create a new ticket if you need help</p>
                  <Button onClick={() => setView('create')} className="bg-indigo-600 hover:bg-indigo-700 text-white">
                    <Plus className="w-4 h-4 mr-2" />Create Ticket
                  </Button>
                </CardContent>
              </Card>
            ) : (
              tickets.map(t => {
                const sc = statusConfig[t.status] || statusConfig.open;
                const pc = priorityConfig[t.priority] || priorityConfig.medium;
                return (
                  <Card key={t.id} className="border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all cursor-pointer" onClick={() => openTicket(t)} data-testid={`ticket-item-${t.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1 flex-wrap">
                            <span className="text-xs font-mono text-gray-400">#{t.ticket_no}</span>
                            <Badge className={sc.color}><sc.icon className="w-3 h-3 mr-1" />{sc.label}</Badge>
                            <Badge className={pc.color}>{pc.label}</Badge>
                            <Badge variant="outline" className="text-gray-500 text-xs">{t.category}</Badge>
                          </div>
                          <h3 className="font-semibold text-gray-900 truncate">{t.subject}</h3>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                            <span>{formatDate(t.created_at)}</span>
                            <span>{t.messages_count} messages</span>
                            {t.last_reply_by === 'support' && <Badge className="bg-blue-100 text-blue-700 text-xs">New reply</Badge>}
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      </div>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        )}

        {/* Ticket Detail */}
        {view === 'detail' && (
          <div className="space-y-4" data-testid="ticket-detail">
            {detailLoading ? (
              <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 text-indigo-500 animate-spin" /></div>
            ) : ticketDetail ? (
              <>
                <Card className="border-gray-200">
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <div>
                        <span className="text-sm font-mono text-gray-400">Ticket #{ticketDetail.ticket_no}</span>
                        <CardTitle className="text-gray-900 mt-1">{ticketDetail.subject}</CardTitle>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          <Badge className={(statusConfig[ticketDetail.status] || statusConfig.open).color}>
                            {(statusConfig[ticketDetail.status] || statusConfig.open).label}
                          </Badge>
                          <Badge className={(priorityConfig[ticketDetail.priority] || priorityConfig.medium).color}>
                            {(priorityConfig[ticketDetail.priority] || priorityConfig.medium).label}
                          </Badge>
                          <Badge variant="outline" className="text-gray-500">{ticketDetail.category}</Badge>
                          <span className="text-xs text-gray-400">{formatDate(ticketDetail.created_at)}</span>
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                </Card>

                <div className="space-y-3">
                  {ticketDetail.messages.map((m, i) => (
                    <div key={i} className={`flex ${m.by === 'student' ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                        m.by === 'student'
                          ? 'bg-indigo-600 text-white rounded-br-md'
                          : 'bg-white border border-gray-200 text-gray-900 rounded-bl-md shadow-sm'
                      }`}>
                        <div className={`text-xs mb-1 font-medium ${m.by === 'student' ? 'text-indigo-200' : 'text-blue-600'}`}>
                          {m.by === 'student' ? 'You' : (m.display_name || m.sender_name || 'Support')}
                        </div>
                        {m.message && <p className="text-sm whitespace-pre-wrap">{m.message}</p>}
                        <AttachmentViewer
                          attachments={m.attachments}
                          tokenHeader={authHeader}
                          endpoint="tickets"
                          theme={m.by === 'student' ? 'dark' : 'light'}
                        />
                        <p className={`text-xs mt-1 ${m.by === 'student' ? 'text-indigo-300' : 'text-gray-400'}`}>{formatDate(m.timestamp)}</p>
                      </div>
                    </div>
                  ))}
                </div>

                {ticketDetail.status !== 'closed' ? (
                  <Card className="border-gray-200">
                    <CardContent className="p-3 space-y-2">
                      {attachFiles.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {attachFiles.map((f, i) => (
                            <div key={i} className="flex items-center gap-1 bg-indigo-50 border border-indigo-200 rounded px-2 py-1 text-xs text-indigo-700">
                              <ImageIcon className="w-3 h-3" />
                              <span className="max-w-[120px] truncate">{f.name}</span>
                              <button onClick={() => removeFile(i)} className="text-red-500 hover:text-red-700"><X className="w-3 h-3" /></button>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2">
                        <textarea value={replyText} onChange={e => setReplyText(e.target.value)} placeholder="Type your reply..." rows={2}
                          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-gray-900 resize-none text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" data-testid="ticket-reply-input" />
                        <div className="flex flex-col gap-1 self-end">
                          <label className="cursor-pointer bg-gray-100 hover:bg-gray-200 border border-gray-300 rounded-lg p-2 transition-colors" title="Attach screenshot (PNG/JPG, max 2MB)">
                            <Paperclip className="w-4 h-4 text-gray-600" />
                            <input type="file" accept="image/png,image/jpeg" multiple className="hidden" onChange={handleFileSelect} data-testid="ticket-attach-input" />
                          </label>
                          <Button onClick={handleReply} disabled={replying || (!replyText.trim() && attachFiles.length === 0)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="ticket-reply-btn">
                            <Send className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400">Only PNG/JPG images, max 2MB each, max 2 files</p>
                    </CardContent>
                  </Card>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm bg-gray-100 rounded-lg">
                    <CheckCircle className="w-5 h-5 inline mr-2 text-emerald-500" />This ticket has been closed
                  </div>
                )}
              </>
            ) : <p className="text-center text-gray-500">Ticket not found</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportPage;
